import useSWR from "swr";
import ecommerceService, { ECOMMERCE_ENDPOINTS } from "../services/ecommerceService";

/**
 * Hook declarativo para carga inicial, caché y refetch del catálogo público de una tienda.
 */
export function usePublicStoreCatalog(
  storeSlug?: string | null,
  params?: { search?: string | null; limit?: number | null; offset?: number | null }
) {
  const { search = "", limit = 50, offset = 0 } = params || {};
  const swrKey = storeSlug
    ? `${ECOMMERCE_ENDPOINTS.PUBLIC_CATALOG}/${storeSlug}/${search || ""}/${limit || 50}/${offset || 0}`
    : null;

  const fetcher = async () => {
    const res = await ecommerceService.getPublicCatalog({
      slug: storeSlug,
      search: search || undefined,
      limit: limit || undefined,
      offset: offset || undefined,
    });
    if (res?.error || !res?.store) {
      throw new Error(res?.message || "No se pudo cargar la tienda solicitada.");
    }
    return res;
  };

  const { data, error, isLoading, mutate } = useSWR(swrKey, fetcher, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
    keepPreviousData: true,
  });

  return {
    store: data?.store || null,
    catalog: data?.catalog || data?.items || [],
    total: data?.total || 0,
    error: error ? (error as Error).message : null,
    isLoading,
    refetch: mutate,
  };
}

/**
 * Hook declarativo para consultar y refrescar el estado de seguimiento de un pedido público.
 */
export function usePublicOrderTracking(trackingToken?: string | null, storeSlug?: string | null) {
  const swrKey = trackingToken
    ? `${ECOMMERCE_ENDPOINTS.PUBLIC_TRACKING}/${trackingToken}/${storeSlug || ""}`
    : null;

  const fetcher = async () => {
    const res = await ecommerceService.getPublicOrderTracking({
      tracking_token: trackingToken!,
      store_slug: storeSlug,
    });
    if (res?.error || !res?.order) {
      throw new Error(res?.message || "No se encontró información del pedido.");
    }
    return res;
  };

  const { data, error, isLoading, mutate } = useSWR(swrKey, fetcher, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });

  return {
    order: data?.order || null,
    error: error ? (error as Error).message : null,
    isLoading,
    refetch: mutate,
  };
}

/**
 * Hook administrativo (con sesión ERP) para listar y refrescar las tiendas online de una empresa.
 */
export function useEcommerceStores(
  tokenData: any,
  params: { company_id?: number | null; company?: string | null }
) {
  const { company_id } = params || {};
  const swrKey = company_id
    ? `${ECOMMERCE_ENDPOINTS.GET_STORES_BY_COMPANY}/${company_id}`
    : null;

  const fetcher = async () => {
    const res = await ecommerceService.getStoresByCompany(tokenData, params);
    if (res?.error) {
      throw new Error(res.message);
    }
    return res;
  };

  const { data, error, isLoading, mutate } = useSWR(
    () => (tokenData?.key && company_id ? [swrKey, tokenData?.token?.uid] : null),
    fetcher,
    { keepPreviousData: true }
  );

  return {
    stores: data?.stores || [],
    error,
    isLoading,
    mutate,
  };
}

/**
 * Hook administrativo (con sesión ERP) para consultar y refrescar los elementos configurados en una tienda.
 */
export function useEcommerceStoreItems(
  tokenData: any,
  params: {
    store_id?: number | null;
    company?: string | null;
  }
) {
  const { store_id } = params || {};
  const swrKey = store_id
    ? `${ECOMMERCE_ENDPOINTS.GET_STORE_ITEMS}/${store_id}`
    : null;

  const fetcher = async () => {
    const res = await ecommerceService.getStoreItems(tokenData, params);
    if (res?.error) {
      throw new Error(res.message);
    }
    return res;
  };

  const { data, error, isLoading, mutate } = useSWR(
    () => (tokenData?.key && store_id ? [swrKey, tokenData?.token?.uid] : null),
    fetcher,
    { keepPreviousData: true }
  );

  return {
    items: data?.items || [],
    error,
    isLoading,
    mutate,
  };
}

/**
 * Hook administrativo (con sesión ERP) para listar y refrescar los pedidos web recibidos.
 */
export function useEcommerceOrders(
  tokenData: any,
  params: {
    company_id?: number | null;
    store_id?: number | null;
    status?: string | null;
    company?: string | null;
  }
) {
  const { company_id, store_id, status } = params || {};
  const swrKey =
    company_id && store_id
      ? `${ECOMMERCE_ENDPOINTS.GET_ORDERS}/${company_id}/${store_id}/${status || "ALL"}`
      : null;

  const fetcher = async () => {
    const res = await ecommerceService.getOrders(tokenData, params);
    if (res?.error) {
      throw new Error(res.message);
    }
    return res;
  };

  const { data, error, isLoading, mutate } = useSWR(
    () => (tokenData?.key && swrKey ? [swrKey, tokenData?.token?.uid] : null),
    fetcher,
    { keepPreviousData: true }
  );

  return {
    orders: data?.orders || [],
    error,
    isLoading,
    mutate,
  };
}
